import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAuth } from "@/contexts/AuthContext"
import { getQuestion, setStudentMessage, submitAnswerText } from "@/services/generatedAPIs"
import { useEffect, useState, useMemo, useRef } from "react"
import { WithTranslation, withTranslation } from "react-i18next"
import { CircleCheck, MessageCircleQuestion, MessageCircleReply } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip"
import { Question, StudentMessage, UserInfoResponseRole } from "@/services/generatedAPIs.schemas"
import { cn, getQuestionText, getStandardAnswerText } from "@/lib/utils"
import { generateAnswerSessionToken } from "@/lib/answerSession"
import { BackendQuestionFileJsonKind, LocalImage, processImage, RichText, parseImagesFromFilesJson, wrapDisplaylines } from "@/utils/preprocess"
import { RadioGroup, RadioGroupItem } from "./ui/radio-group"
import { ImageUpload } from "./ui/image-upload"
import { ReadonlyQuestionDisplay } from "./readonly-question-display"

// Declare the math-field element type
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'math-field': any;
    }
  }
}

function StudentMessageDialog({
  t,
  i18n,
  studentMessage,
  className,
  onUpdate,
  isReviewMode = false,
  reviewList = [],
  currentIndex = 0,
  ...props
}: WithTranslation & {
  className?: string,
  onUpdate?: () => void,
  studentMessage: StudentMessage,
  isReviewMode?: boolean,
  reviewList?: StudentMessage[],
  currentIndex?: number
}) {
  const { getCurrentUser } = useAuth();
  const [question, setQuestion] = useState<Question>();
  const [questionBody, setQuestionBody] = useState<RichText>();
  const [standardAnswerBody, setStandardAnswerBody] = useState<RichText>();
  const [answerBody, setAnswerBody] = useState<RichText>();
  const [isCorrect, setIsCorrect] = useState<boolean>(false);
  const [teacherMessageValue, setTeacherMessageValue] = useState<string>(studentMessage.teacherMessage ?? "");
  const lastMessageKeyRef = useRef<string>();

  // 批改导航状态
  const [currentReviewIndex, setCurrentReviewIndex] = useState<number>(currentIndex);
  const [currentStudentMessage, setCurrentStudentMessage] = useState<StudentMessage>(studentMessage);

  // 导航函数
  const goToPrevious = () => {
    if (isReviewMode && currentReviewIndex > 0) {
      const newIndex = currentReviewIndex - 1;
      setCurrentReviewIndex(newIndex);
      setCurrentStudentMessage(reviewList[newIndex]);
    }
  };

  const goToNext = () => {
    if (isReviewMode && currentReviewIndex < reviewList.length - 1) {
      const newIndex = currentReviewIndex + 1;
      setCurrentReviewIndex(newIndex);
      setCurrentStudentMessage(reviewList[newIndex]);
    }
  };

  const handleTeacherMessageChange = (value: string) => {
    setTeacherMessageValue(value);
    if (isReviewMode) {
      setCurrentStudentMessage((prev) => prev ? { ...prev, teacherMessage: value } : prev);
    } else {
      studentMessage.teacherMessage = value;
    }
  };

  const resetTeacherFeedbackState = () => {
    if (getCurrentUser()!.role !== UserInfoResponseRole.TEACHER) {
      return;
    }

    setTeacherMessageValue("");
    setIsCorrect(false);

    if (isReviewMode) {
      setCurrentStudentMessage((prev) => prev ? { ...prev, teacherMessage: "" } : prev);
    } else {
      studentMessage.teacherMessage = "";
    }
  };

  // 检查是否可以导航
  const canGoPrevious = isReviewMode && currentReviewIndex > 0;
  const canGoNext = isReviewMode && currentReviewIndex < reviewList.length - 1;

  // 当点击不同题目时，重置内部状态
  useEffect(() => {
    setCurrentReviewIndex(currentIndex);
    setCurrentStudentMessage(studentMessage);
  }, [currentIndex, studentMessage]);

  useEffect(() => {
    const activeMessage = isReviewMode ? currentStudentMessage : studentMessage;
    const messageKey = `${isReviewMode ? "review" : "single"}-${activeMessage?.id ?? "no-id"}-${activeMessage?.questionId ?? "no-question"}`;

    if (lastMessageKeyRef.current === messageKey) {
      return;
    }

    lastMessageKeyRef.current = messageKey;
    setTeacherMessageValue(activeMessage?.teacherMessage ?? "");
    setIsCorrect(false);
  }, [isReviewMode, currentStudentMessage, studentMessage]);

  // 处理题目图片数据
  const questionImages = useMemo((): LocalImage[] => {
    if (!questionBody?.backendImageFile) {
      return [];
    }
    return questionBody.backendImageFile.map((file, index) => {
      // 根据文件类型推断扩展名
      const getExtensionFromType = (type?: string): string => {
        const typeMap: { [key: string]: string } = {
          'image/jpeg': '.jpg',
          'image/jpg': '.jpg',
          'image/png': '.png',
          'image/gif': '.gif',
          'image/webp': '.webp',
          'image/bmp': '.bmp',
          'image/svg+xml': '.svg'
        };
        return typeMap[type || ''] || '.jpg';
      };

      const extension = file.name && file.name.includes('.') ?
        '.' + file.name.split('.').pop() : getExtensionFromType(file.type);

      return {
        name: file.name || `q-${index + 1}${extension}`, // 使用数字命名，如：q-1.jpg, q-2.png...
        blob: new Blob(),
        type: file.type || 'image/jpeg',
        url: file.url
      };
    }).filter(img => img.url) as LocalImage[];
  }, [questionBody]);

  // 处理标准答案图片数据
  const standardAnswerImages = useMemo((): LocalImage[] => {
    if (!standardAnswerBody?.backendImageFile) {
      return [];
    }
    return standardAnswerBody.backendImageFile.map((file, index) => {
      // 根据文件类型推断扩展名
      const getExtensionFromType = (type?: string): string => {
        const typeMap: { [key: string]: string } = {
          'image/jpeg': '.jpg',
          'image/jpg': '.jpg',
          'image/png': '.png',
          'image/gif': '.gif',
          'image/webp': '.webp',
          'image/bmp': '.bmp',
          'image/svg+xml': '.svg'
        };
        return typeMap[type || ''] || '.jpg';
      };

      const extension = file.name && file.name.includes('.') ?
        '.' + file.name.split('.').pop() : getExtensionFromType(file.type);

      return {
        name: file.name || `a-${index + 1}${extension}`, // 使用数字命名，如：a-1.jpg, a-2.png...
        blob: new Blob(),
        type: file.type || 'image/jpeg',
        url: file.url
      };
    }).filter(img => img.url) as LocalImage[];
  }, [standardAnswerBody]);

  // 处理学生答案图片数据
  const answerImages = useMemo((): LocalImage[] => {
    // 首先尝试从 answerBody.backendImageFile 获取图片
    if (answerBody?.backendImageFile && answerBody.backendImageFile.length > 0) {
      return answerBody.backendImageFile.map((file, index) => {
        // 根据文件类型推断扩展名
        const getExtensionFromType = (type?: string): string => {
          const typeMap: { [key: string]: string } = {
            'image/jpeg': '.jpg',
            'image/jpg': '.jpg',
            'image/png': '.png',
            'image/gif': '.gif',
            'image/webp': '.webp',
            'image/bmp': '.bmp',
            'image/svg+xml': '.svg'
          };
          return typeMap[type || ''] || '.jpg';
        };

        const extension = file.name && file.name.includes('.') ?
          '.' + file.name.split('.').pop() : getExtensionFromType(file.type);

        return {
          name: file.name || `${index + 1}${extension}`, // 使用数字命名，如：1.jpg, 2.png...
          blob: new Blob(),
          type: file.type || 'image/jpeg',
          url: file.url
        };
      }).filter(img => img.url) as LocalImage[];
    }

    // 如果 backendImageFile 为空，尝试直接从 question.filesJson 解析
    if (question?.filesJson) {
      try {
        return parseImagesFromFilesJson(question.filesJson, 'answer');
      } catch (error) {
        console.error('Failed to parse answer images from filesJson:', error);
      }
    }

    return [];
  }, [answerBody, question]);

  const onSubmit = async (overrideTeacherMessage?: string) => {
    const messageToSubmit = isReviewMode ? currentStudentMessage : studentMessage;
    if (!messageToSubmit) {
      return;
    }

    messageToSubmit.teacherMessage = overrideTeacherMessage ?? teacherMessageValue;
    messageToSubmit.replyType = 1;

    // Handle type 0 (student question) - submit correction
    if (messageToSubmit.type === 0 && question && getCurrentUser()!.role === UserInfoResponseRole.TEACHER) {
      question.isCorrectAnswer = isCorrect;
      question.teacherReview = messageToSubmit.teacherMessage;
      const rsp = await submitAnswerText({ model: JSON.stringify({ question: question, answerSessionId: generateAnswerSessionToken({ studentId: question.studentId, questionId: question.id }) }) });
      if (rsp.code === 200 && rsp.data) {
      } else {
        return;
      }
    }

    // Handle type 1 (AI tutor question) - submit correction
    if (messageToSubmit.type === 1 && question && answerBody) {
      question.isCorrectAnswer = isCorrect;
      question.teacherReview = messageToSubmit.teacherMessage;
      const rsp = await submitAnswerText({ model: JSON.stringify({ question: question, answerSessionId: generateAnswerSessionToken({ studentId: question.studentId, questionId: question.id }) }) });
      if (rsp.code === 200 && rsp.data) {
      } else {
        return;
      }
    }

    setStudentMessage({
      studentMessage: messageToSubmit
    }).then((res) => {
      if (res.code === 200) {
        console.log("setStudentMessage successful", res.data);
        onUpdate && onUpdate();
        resetTeacherFeedbackState();

        // 批改模式下的自动跳转逻辑
        if (isReviewMode && canGoNext) {
          // 有下一题，自动跳转到下一题
          goToNext();
        }
        // 如果没有下一题或不是批改模式，DialogClose会自动关闭弹窗
      } else {
        // Handle login error
        console.error("setStudentMessage failed", res.code, res.message);
      }
    }
    ).catch((error) => {
      console.error("setStudentMessage error", error);
    }
    ).finally(() => {
    });
  };

  useEffect(() => {
    const messageToLoad = isReviewMode ? currentStudentMessage : studentMessage;
    if (messageToLoad.questionId) {
      getQuestion({
        studentId: getCurrentUser()!.id,
        questionId: messageToLoad.questionId!,
      }).then((rsp) => {
        if (rsp.code === 200 && rsp.data) {
          const questionModel = rsp.data.questionModel;
          setQuestion(questionModel.question);
          setQuestionBody(processImage(t, true, BackendQuestionFileJsonKind.QuestionImage, questionModel.questionInfo.filesJson, getQuestionText(questionModel.questionInfo, i18n)))
          setStandardAnswerBody(processImage(t, true, BackendQuestionFileJsonKind.StandardAnswerImage, questionModel.questionInfo.filesJson, getStandardAnswerText(questionModel.questionInfo, i18n)));
          setAnswerBody(processImage(t, true, BackendQuestionFileJsonKind.AnswerImage, questionModel.question?.filesJson, questionModel.question?.answer));
        }
      })
    }
  }, [currentStudentMessage, studentMessage, isReviewMode])

  return (
    <div className={cn("flex items-center", className)}>
      <Dialog>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="relative flex items-center justify-center"
                >
                  {
                    getCurrentUser()!.role === UserInfoResponseRole.STUDENT ?
                      <MessageCircleQuestion className="h-[25px] w-[25px] cursor-pointer text-[#2C2C2C] transition-opacity hover:opacity-60" />
                      : <MessageCircleReply />
                  }
                </button>
              </DialogTrigger>
            </TooltipTrigger>
            <TooltipContent
              className="rounded-md bg-[var(--practice-dialog-accent)] px-3 py-1.5 text-xs font-bold text-[var(--practice-dialog-accent-fg)] shadow-md"
              arrowClassName="bg-[var(--practice-dialog-accent)] fill-[var(--practice-dialog-accent)]"
            >
              {
                getCurrentUser()!.role === UserInfoResponseRole.STUDENT ?
                  t('component.askteacher')
                  : t('component.replyonline')
              }
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <DialogContent className="sm:max-w-[1024px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            {/* 批改模式下显示题数信息和导航 */}
            {isReviewMode ? (
              <div className="flex items-center justify-between">
                {/* 左侧上一页按钮 */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPrevious}
                  disabled={!canGoPrevious}
                  className="flex items-center justify-center w-8 h-8 p-0"
                >
                  <span className="text-lg font-bold">&lt;</span>
                </Button>

                {/* 中间题数显示 */}
                <DialogTitle className="text-center">
                  <span className="text-lg font-semibold">
                    {currentReviewIndex + 1} / {reviewList.length}
                  </span>
                </DialogTitle>

                {/* 右侧下一页按钮 */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNext}
                  disabled={!canGoNext}
                  className="flex items-center justify-center w-8 h-8 p-0"
                >
                  <span className="text-lg font-bold">&gt;</span>
                </Button>
              </div>
            ) : (
              <DialogTitle>
                {
                  getCurrentUser()!.role === UserInfoResponseRole.STUDENT ?
                    t('component.askteacher')
                    : t('component.studentmsg')
                }
              </DialogTitle>
            )}
            {/* <DialogDescription>
              Make changes to your profile here. Click save when you&apos;re
              done.
            </DialogDescription> */}
          </DialogHeader>
          <div className="flex flex-col grow space-y-6 overflow-x-hidden">
            {/* 题目部分 */}
            <div className="border-b border-gray-200 pb-4">
              <div className="mb-2">
                <Label htmlFor="question" className="text-base font-medium">
                  {t("common.question")}
                </Label>
              </div>

              <div className="mb-4 overflow-x-hidden">
                {
                  questionBody?.text &&
                  <ReadonlyQuestionDisplay questionText={questionBody.text} className="break-words" />
                }
              </div>

              {questionImages.length > 0 && (
                <div>
                  <ImageUpload
                    disabled={true}
                    initialImages={questionImages}
                    maxImages={8}
                    imageType="question"
                    isEditMode={true}
                  />
                </div>
              )}
            </div>

            {/* 标准答案部分 */}
            <div className="border-b border-gray-200 pb-4">
              <div className="mb-2">
                <Label htmlFor="standardAnswer" className="text-base font-medium">
                  {t("common.standardanswer", { defaultValue: "標準答案" })}
                </Label>
              </div>

              <div className="mb-4">
                <math-field
                  read-only
                  className="w-full border-gray-200 border-2 rounded-md min-h-[100px]"
                >
                  {wrapDisplaylines(standardAnswerBody?.text)}
                </math-field>
              </div>

              {standardAnswerImages.length > 0 && (
                <div>
                  <ImageUpload
                    disabled={true}
                    initialImages={standardAnswerImages}
                    maxImages={8}
                    imageType="standardAnswer"
                    isEditMode={true}
                  />
                </div>
              )}
            </div>

            {/* 学生答案部分 */}
            <div>
              <div className="mb-2">
                <Label htmlFor="studentAnswer" className="text-base font-medium">
                  {t("common.studentanswer", { defaultValue: "學生答案" })}
                </Label>
              </div>

              <div className="mb-4">
                <math-field
                  read-only
                  className="w-full border-gray-200 border-2 rounded-md min-h-[100px]"
                >
                  {wrapDisplaylines(answerBody?.text)}
                </math-field>
              </div>

              {answerImages.length > 0 && (
                <div>
                  <ImageUpload
                    disabled={true}
                    initialImages={answerImages}
                    maxImages={8}
                    imageType="answer"
                    isEditMode={true}
                  />
                </div>
              )}
            </div>
          </div>

          {
            (isReviewMode ? currentStudentMessage : studentMessage).type === 0 && (
              <>
                <div className="grid gap-3">
                  <Input
                    readOnly={getCurrentUser()!.role !== UserInfoResponseRole.STUDENT}
                    id="description"
                    name="description"
                    defaultValue={(isReviewMode ? currentStudentMessage : studentMessage).description}
                    onChange={(e) => {
                      if (isReviewMode) {
                        currentStudentMessage.description = e.target.value;
                      } else {
                        studentMessage.description = e.target.value;
                      }
                    }}
                  />
                </div>
                {
                  (teacherMessageValue || getCurrentUser()!.role === UserInfoResponseRole.TEACHER) &&
                  <div className="grid gap-3">
                    <Label htmlFor="teacherMessage">{t('component.teachermsg')}</Label>
                    <Input
                      readOnly={getCurrentUser()!.role !== UserInfoResponseRole.TEACHER}
                      id="teacherMessage"
                      name="teacherMessage"
                      value={teacherMessageValue}
                      onChange={(e) => handleTeacherMessageChange(e.target.value)}
                    />
                  </div>
                }
                {
                  getCurrentUser()!.role === UserInfoResponseRole.TEACHER && (
                    <RadioGroup value={isCorrect ? "correct" : "wrong"} onValueChange={(value) => setIsCorrect(value === "correct")}>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="correct" id="r1-type0" />
                        <Label htmlFor="r1-type0">{t('component.correct')}</Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="wrong" id="r2-type0" />
                        <Label htmlFor="r2-type0">{t('component.wrong')}</Label>
                      </div>
                    </RadioGroup>
                  )
                }
              </>
            )
          }

          {
            (isReviewMode ? currentStudentMessage : studentMessage).type === 1 && (
              <>
                <RadioGroup value={isCorrect ? "correct" : "wrong"} onValueChange={(value) => setIsCorrect(value === "correct")}>
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="correct" id="r1" />
                    <Label htmlFor="r1">{t('component.correct')}</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="wrong" id="r2" />
                    <Label htmlFor="r2">{t('component.wrong')}</Label>
                  </div>
                </RadioGroup>
                {
                  (teacherMessageValue || getCurrentUser()!.role === UserInfoResponseRole.TEACHER) &&
                  <div className="grid gap-3">
                    <Label htmlFor="teacherMessage">{t('component.comment')}</Label>
                    <Input
                      readOnly={getCurrentUser()!.role !== UserInfoResponseRole.TEACHER}
                      id="teacherMessage"
                      name="teacherMessage"
                      value={teacherMessageValue}
                      onChange={(e) => handleTeacherMessageChange(e.target.value)}
                    />
                  </div>
                }
              </>
            )
          }
          <DialogFooter className="flex justify-center gap-3 sm:gap-4">
            <DialogClose asChild>
              <Button
                variant="outline"
                className="h-[44px] min-w-[100px] rounded-[15px] border-[#E5E7EB] bg-white text-sm font-medium text-[#9CA3AF] shadow-none hover:bg-gray-50 hover:text-[#6B7280] xl:h-12 xl:min-w-[110px] xl:text-base 2xl:h-[53px] 2xl:min-w-[117px] 2xl:text-xl"
              >
                {t('component.cancel')}
              </Button>
            </DialogClose>
            {/* 批改模式下，只有在最后一题时才自动关闭弹窗 */}
            {isReviewMode && canGoNext ? (
              <Button
                type="submit"
                onClick={() => onSubmit()}
                className="h-[44px] min-w-[100px] rounded-[15px] bg-[var(--practice-dialog-accent)] text-sm font-semibold text-[var(--practice-dialog-accent-fg)] shadow-none hover:bg-[var(--practice-dialog-accent)] hover:brightness-95 xl:h-12 xl:min-w-[110px] xl:text-base 2xl:h-[53px] 2xl:min-w-[117px] 2xl:text-xl"
              >
                {t('common.save')}
              </Button>
            ) : (
              <DialogClose asChild>
                <Button
                  type="submit"
                  onClick={() => onSubmit()}
                  className="h-[44px] min-w-[100px] rounded-[15px] bg-[var(--practice-dialog-accent)] text-sm font-semibold text-[var(--practice-dialog-accent-fg)] shadow-none hover:bg-[var(--practice-dialog-accent)] hover:brightness-95 xl:h-12 xl:min-w-[110px] xl:text-base 2xl:h-[53px] 2xl:min-w-[117px] 2xl:text-xl"
                >
                  {t('common.save')}
                </Button>
              </DialogClose>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {
        (getCurrentUser()!.role === UserInfoResponseRole.TEACHER) && (studentMessage.type === 0) &&
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleCheck onClick={() => {
                onSubmit(t('component.replyoffline'));
              }}></CircleCheck>
            </TooltipTrigger>
            <TooltipContent>
              {t('component.replyoffline')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      }
    </div >
  )
}

export default withTranslation()(StudentMessageDialog);
